// ============================================================
// People Database — localStorage-backed custom overrides
// รูปแบบ custom store: { "email@dtc.co.th": { dept?, jobTitle?, name? } }
// Custom > Azure AD API เสมอ
// ============================================================

const STORAGE_KEY = "dtc_people_custom_v1";
let serverDirectory = null;
export function setServerDirectory(people) {
  if (Array.isArray(people) && (serverDirectory || people.some(p => p.rosterManaged))) serverDirectory = people;
}

const SKIP_PATTERNS = [
  "spo-grid", "sharepoint", "nt service\\",
  "businessdevelopdept@", "app@sharepoint",
];

export function isRealUser(u) {
  if (!u?.name || !u?.email) return false;
  const e = u.email.toLowerCase();
  if (e === "true" || e === "false") return false;
  return !SKIP_PATTERNS.some(p => e.includes(p.toLowerCase()));
}

// ─── Read/Write ───────────────────────────────────────────────
export function getCustomStore() {
  try {
    const store = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (serverDirectory) for (const value of Object.values(store)) { delete value.dept; delete value.jobTitle; }
    return store;
  }
  catch { return {}; }
}

function writeStore(s) { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); }

/**
 * บันทึก custom fields เช่น { dept: "Sales", jobTitle: "Manager" }
 * ส่ง value ว่าง ("") เพื่อล้างค่านั้น
 */
export function saveCustomPerson(email, fields) {
  if (!email) return;
  const store = getCustomStore();
  const key   = email.toLowerCase();
  const entry = { ...(store[key] ?? {}) };
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") entry[k] = v;
    else delete entry[k];
  });
  if (Object.keys(entry).length === 0) delete store[key];
  else store[key] = entry;
  writeStore(store);
}

export function clearCustomPerson(email) {
  if (!email) return;
  const store = getCustomStore();
  delete store[email.toLowerCase()];
  writeStore(store);
}

// ─── Apply custom overrides to userMap ───────────────────────
// Input/Output: { [sharePointId]: { name, email, dept, jobTitle } }
export function applyCustomToUserMap(userMap) {
  const custom = getCustomStore();
  const result = {};
  Object.entries(userMap).forEach(([id, u]) => {
    if (!u.email) { result[id] = u; return; }
    const ov = custom[u.email.toLowerCase()] ?? {};
    result[id] = {
      ...u,
      name:     ov.name     ?? u.name,
      dept:     ov.dept     ?? u.dept     ?? "",
      jobTitle: ov.jobTitle ?? u.jobTitle ?? "",
    };
  });
  return result;
}

// ─── People list for admin table ─────────────────────────────
// แหล่งข้อมูลหลัก = Local DB (custom store) — คนที่ผ่านการ import แล้วเท่านั้นจะโผล่ในตาราง
// rawUserMap (SharePoint/Graph) ใช้แค่เสริมคอลัมน์ "API" ให้คนที่ยังมีตัวตนอยู่ใน SP
export function getPeopleList(rawUserMap) {
  if (serverDirectory) return serverDirectory;
  const custom = getCustomStore();

  const emailToApi = {};
  Object.entries(rawUserMap).forEach(([spId, u]) => {
    if (u.email && isRealUser(u)) emailToApi[u.email.toLowerCase()] = { ...u, _spId: spId };
  });

  return Object.entries(custom)
    .map(([email, ov]) => {
      const api = emailToApi[email];
      return {
        // id ใช้ SP GUID ถ้าเจอ (สำหรับหารูปจาก photoMap) ไม่งั้น fallback เป็น email
        id:             api?._spId ?? email,
        name:           ov.name || api?.name || email,
        nameApi:        api?.name ?? "",   // ชื่อ eng จาก SP — เก็บแยกไว้ใช้ค้นหา ไม่ override name ไทย
        email,
        deptApi:        api?.dept ?? "",
        deptCustom:     ov.dept ?? "",
        dept:           ov.dept || api?.dept || "",
        jobTitleApi:    api?.jobTitle ?? "",
        jobTitleCustom: ov.jobTitle ?? "",
        jobTitle:       ov.jobTitle || api?.jobTitle || "",
        lineAccount:    ov.lineAccount ?? "",
        nicknames:      ov.nicknames   ?? "",
        division:       ov.division    ?? "",
        employeeCode:   ov.employeeCode ?? "",
        gender:         ov.gender      ?? "",
        phone:          ov.phone       ?? "",
        _inSP:          !!api,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "th"));
}

// ─── Nickname lookup: ค้นหาชื่อจาก nickname ────────────────
// ใช้โดย Gemini pipeline เพื่อ resolve "พี่จัมป์" → "Prawit Korsri"
export function findPersonByNickname(nickname) {
  const custom = getCustomStore();
  const q = nickname.trim().toLowerCase();
  for (const [email, ov] of Object.entries(custom)) {
    const names = (ov.nicknames ?? "").split(",").map(n => n.trim().toLowerCase()).filter(Boolean);
    if (names.includes(q)) return { email, ...ov };
    if ((ov.lineAccount ?? "").toLowerCase() === q) return { email, ...ov };
  }
  return null;
}
